import { GuideEntityMapper } from '../guide/entityMapper'
import { SpotEntityMapper } from '../spot/entityMapper'
import {
	ApiItinerary,
	ApiItineraryCategory,
	ApiItineraryDay,
	ApiItineraryGroup,
	ApiItineraryMarker,
	ApiItineraryOptions,
	ApiItinerarySpot,
	ApiProduct,
	APITripTask,
} from './apiTypes'
import ItineraryCategory from '~/models/ItineraryCategory'
import ItineraryGroup from '~/models/ItineraryGroup'
import Currency from '~/models/Currency'
import Price from '~/models/Price'
import Product from '~/models/Product'
import {
	Itinerary2 as Itinerary,
	TRIP_PHASE,
	TRIP_TASK_STATE,
	TRIP_ACTIONS,
	TripOptions,
	TRIP_ROLES,
	TripTask,
	Itinerary2,
} from '~/models/Itinerary'
import { ItinerarySpot2 as ItinerarySpot } from '~/models/ItinerarySpot'
import { Marker2 } from '~/models/Marker'
import { PlaceClassification } from '~/models/Place'
import { removeUndefinedValues } from '~/models/helpers/removeUndefined'
import { TravelerEntityMapper } from '../traveler/entityMapper'
import { ItineraryDay } from '~/models/ItineraryDay'
import { MediaEntityMapper } from '../media/entityMapper'
import { DestinationMapperEntity } from '../destination/toEntity'

export class ItineraryEntityMapper {
	static toTripOptions(options: ApiItineraryOptions): TripOptions {
		return {
			actions: options.actions as TRIP_ACTIONS[],
			isRemovable: options.actions.includes('DELETE'),
			role: options.role as TRIP_ROLES,
			bookings: options.bookings,
			checklist: options.checklist,
			discover: options.discover,
			trip: options.trip,
			name: options.name,
		}
	}

	static toItinerary(apiItinerary: ApiItinerary): Itinerary {
		let coverMedia
		if (apiItinerary.cover_media) coverMedia = MediaEntityMapper.toMedia(apiItinerary.cover_media)
		let destination
		if (apiItinerary.destination)
			destination = DestinationMapperEntity.toDestination(apiItinerary.destination)

		const itinerary: Itinerary2 = {
			id: apiItinerary.id || null,
			referenceType: 'itinerary',
			covers: apiItinerary.covers || [],
			numDays: apiItinerary.num_days,
			numNotes: apiItinerary.num_notes,
			numDestinations: apiItinerary.num_destinations || 0,
			numSpots: apiItinerary.num_spots || 0,
			numTravelers: apiItinerary.num_travelers || 1,
			isPrivate: apiItinerary.state === 'private',
			name: apiItinerary.title || '',
			subtitle: apiItinerary.subtitle,
			description: apiItinerary.description,
			startDate: apiItinerary.start_date,
			endDate: apiItinerary.end_date,
			travelersPictures: apiItinerary.travelers_pictures || [],
			agencyId: apiItinerary.agency_id,
			cover: apiItinerary.cover,
			isUserCover: Boolean(apiItinerary.cover_id),
			coverMedia,
			creatorId: apiItinerary.creator_id,
			numViews: apiItinerary.num_views,
			destination,
			phase: this.mapTripPhase(apiItinerary.phase),
			declaredTravelers: apiItinerary.num_declared_travelers,
			potentialTravelers: apiItinerary.num_potential_travelers,
			enterpriseLogo: apiItinerary.enterprise_logo,
			inspirationSource: apiItinerary.sources?.length
				? GuideEntityMapper.toInspirationSources(apiItinerary.sources)[0]
				: undefined,
		}
		return itinerary
	}

	private static mapTripPhase(phase?: string): TRIP_PHASE {
		const phaseMap: Record<string, TRIP_PHASE> = {
			future: TRIP_PHASE.NEXT,
			past: TRIP_PHASE.PAST,
			present: TRIP_PHASE.CURRENT,
			no_dates: TRIP_PHASE.NO_DATES,
		}
		return phase ? phaseMap[phase]! : TRIP_PHASE.NO_DATES
	}

	static toItineraries(itineraries: ApiItinerary[]): Itinerary[] {
		return itineraries.map((itinerary: any) => this.toItinerary(itinerary))
	}

	static parseItineraryMarker(marker: ApiItineraryMarker, itineraryMetadata?: boolean): Marker2 {
		return {
			id: marker.id,
			placeId: marker.place_id,
			location: marker.location,
			isReference: marker.metadata.is_reference,
			type: this.toType(marker.type),
			label: marker.label,
			isSaved: marker.metadata.is_saved,
			position: marker.metadata.order,
			labelColor: marker.metadata.label_color,
			color: itineraryMetadata ? marker.metadata.colors?.[0] : undefined,
		}
	}

	static parseItineraryMarkers = (
		itineraryMarkers: ApiItineraryMarker[],
		itineraryMetadata?: boolean
	): Marker2[] => {
		return itineraryMarkers.map((itineraryMarker: ApiItineraryMarker) =>
			this.parseItineraryMarker(itineraryMarker, itineraryMetadata)
		)
	}

	static parseDayMarker(marker: ApiItineraryMarker): Marker2 {
		const color = marker.metadata.colors ? marker.metadata.colors[0] : undefined
		return {
			...this.parseItineraryMarker(marker),
			color,
		}
	}

	static parseDayMarkers = (dayMarkers: ApiItineraryMarker[]): Marker2[] => {
		return dayMarkers.map((dayMarker: ApiItineraryMarker) => this.parseDayMarker(dayMarker))
	}

	static toItinerarySpot({
		spot,
		groups,
		categories,
		is_reference: isReference,
	}: ApiItinerarySpot): ItinerarySpot {
		const parsedSpot = SpotEntityMapper.toSpot(spot)
		const parsedGroups = groups && this.parseItineraryGroups(groups)
		const parsedCategories = categories && this.parseItineraryCategories(categories)
		return {
			...parsedSpot,
			isReference,
			groups: parsedGroups,
			categories: parsedCategories,
		}
	}

	static parseItinerarySpots = (itinerarySpots: ApiItinerarySpot[]): ItinerarySpot[] => {
		return itinerarySpots.map((itinerarySpot: ApiItinerarySpot) =>
			this.toItinerarySpot(itinerarySpot)
		)
	}

	static parseItineraryGroup(itineraryGroup: ApiItineraryGroup): ItineraryGroup {
		return {
			id: itineraryGroup.id,
			itineraryId: itineraryGroup.itinerary_id,
			name: itineraryGroup.text,
			color: itineraryGroup.color,
			lightColor: itineraryGroup.light_color,
		}
	}

	static parseItineraryGroups(itineraryGroups: ApiItineraryGroup[]): ItineraryGroup[] {
		return itineraryGroups.map((itineraryGroup) => this.parseItineraryGroup(itineraryGroup))
	}

	static parseItineraryCategory(itineraryCategory: ApiItineraryCategory): ItineraryCategory {
		return new ItineraryCategory({
			id: itineraryCategory.id,
			name: itineraryCategory.name,
			logo: itineraryCategory.logo,
		})
	}

	static parseItineraryCategories(
		itineraryCategories: ApiItineraryCategory[]
	): ItineraryCategory[] {
		return itineraryCategories.map((itineraryCategory) =>
			this.parseItineraryCategory(itineraryCategory)
		)
	}

	static toProduct = (product: ApiProduct) => {
		let price
		if (product.prices?.length) {
			const apiPrice = product.prices[0]
			const currency = new Currency({ code: apiPrice!.currency })
			price = new Price({
				currency,
				value: apiPrice!.price,
			})
		}

		const mappedProduct = {
			id: product.id,
			title: product.title,
			category: product.category,
			subcategory: product.subcategory,
			url: product.external_url,
			coverUrls: product.cover_urls,
			price,
		} as Product
		return mappedProduct
	}

	static toProducts = (products: ApiProduct[]) => {
		return products.map((product: ApiProduct) => this.toProduct(product))
	}

	static toType(type: string): PlaceClassification {
		const markerType: PlaceClassification = spotTypes[type]!
		if (!markerType) return 'touristAttraction'
		return markerType
	}

	static toInvitationRole(role?: string): FormData {
		const formData = new FormData()
		formData.append('role', role || 'viewer')
		return formData
	}

	static toTask(task: APITripTask): TripTask {
		const result: TripTask = {
			id: task.id,
			title: task.title,
			description: task.description,
			dueDate: task.due_date ? new Date(task.due_date) : undefined,
			state: task.state as TRIP_TASK_STATE,
			modified: task.modified,
			assigneesCount: task.assignees_count,
			assignees: TravelerEntityMapper.toTravelers(task.assignees || []),
		}
		return removeUndefinedValues(result)
	}

	static toTasks(tasks: APITripTask[]): TripTask[] {
		return tasks.map((task) => this.toTask(task))
	}

	static toDay(apiDay: ApiItineraryDay): ItineraryDay {
		const day: ItineraryDay = {
			id: apiDay.id,
			dayNumber: apiDay.day_number,
			color: apiDay.color,
			description: apiDay.description,
			spotsCount: apiDay.spots_count,
			date: new Date(apiDay.date),
		}
		return removeUndefinedValues(day)
	}

	static toDays(apiDays: ApiItineraryDay[]): ItineraryDay[] {
		return apiDays.map((day) => this.toDay(day))
	}
}

const spotTypes: Record<string, PlaceClassification> = {
	entertainment: 'culture',
	place_of_worship: 'touristAttraction',
	nature: 'outdoorLocation',
	attraction: 'touristAttraction',
	accommodation: 'accommodation',
	transport: 'transport',
	food: 'food',
	drinks: 'drink',
	art_and_culture: 'culture',
	services: 'service',
	coast: 'outdoorLocation',
	parks: 'outdoorLocation',
	sports: 'service',
}
